import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { colorsPalettes } from "@similarweb/styles";
import { Combobox, IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { MenuItemType } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { useDeleteCampaignModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/delete/useDeleteCampaignModal";
import { useEditCampaignModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/useEditCampaignModal";
import { useSubscribeMenuItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/subscribe/useSubscribeMenuItem";
import { isWeeklyReportSubscribed } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { useAssetPermissions } from "app/services/asset-permissions/AssetPermissions";
import { allTrackers } from "app/services/track/track";

import { useLLMTrackerCampaignById } from "../shared/llm-tracker-service/useLLMTrackerCampaigns";

import { getMenuItems } from "./consts";

export const CampaignMenu: FC = () => {
    const {
        params: { campaignId, settings },
    } = useCurrentStateAndParams();

    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const { data: selectedCampaign } = useLLMTrackerCampaignById(campaignId);
    const permissions = useAssetPermissions(selectedCampaign);

    const { modal: editModal, openModal: openEditModal } = useEditCampaignModal();
    const { modal: deleteModal, openModal: openDeleteModal } = useDeleteCampaignModal();
    const { handleSubscribe, isPending: isSubscribePending } = useSubscribeMenuItem();

    const menuItems = getMenuItems({
        canEdit: permissions?.canEdit,
        canDelete: permissions?.canDelete,
        campaign: selectedCampaign ?? undefined,
        isSubscribePending,
    });

    // Open the edit modal once when the URL indicates it on mount (e.g. email unsubscribe flow).
    // Guarded by a ref so campaign query refetches don't reopen the modal and wipe unsaved edits.
    const hasOpenedFromUrl = useRef(false);
    useEffect(() => {
        if (settings === "true" && selectedCampaign?.id && !hasOpenedFromUrl.current) {
            hasOpenedFromUrl.current = true;
            openEditModal(selectedCampaign, !permissions?.canEdit);
        }
    }, [settings, selectedCampaign, openEditModal, permissions]);

    const onItemClick = (item: { id: MenuItemType }) => {
        setIsMenuOpen(false);

        switch (item.id) {
            case MenuItemType.Edit:
                allTrackers.dispatchTrackEvent("Drop Down", "Click", "Edit");
                openEditModal(selectedCampaign, !permissions?.canEdit);
                break;

            case MenuItemType.Subscribe:
                if (selectedCampaign) {
                    handleSubscribe(selectedCampaign, !isWeeklyReportSubscribed(selectedCampaign.subscriptions));
                }
                break;

            case MenuItemType.Delete:
                allTrackers.dispatchTrackEvent("Drop Down", "Click", "Delete");
                openDeleteModal(selectedCampaign);
                break;
        }
    };

    return (
        <Flex
            position="absolute"
            right="5px"
            top="12px"
        >
            <Combobox
                width="auto"
                placement="bottom-start"
                isOpen={isMenuOpen}
                onOpenChange={setIsMenuOpen}
                data-automation="campaign-menu-dropdown"
            >
                <Combobox.Trigger>
                    <Flex width="100%">
                        <IconButton
                            type="flat"
                            iconName="dots-more"
                            iconSize="xs"
                            data-automation="campaign-menu-button"
                        />
                    </Flex>
                </Combobox.Trigger>
                <Combobox.Content>
                    {Object.entries(menuItems).map(([id, { text, icon: Icon, isEnabled }]) => (
                        <Combobox.Option
                            key={id}
                            id={id}
                            textValue={text}
                            isDisabled={!isEnabled}
                            onClick={() => onItemClick({ id: id as MenuItemType })}
                            data-automation={`campaign-menu-item-${id}`}
                        >
                            <Flex
                                alignItems="center"
                                gap="8px"
                            >
                                <Icon
                                    size="xs"
                                    color={isEnabled ? colorsPalettes.carbon[200] : colorsPalettes.midnight[50]}
                                />
                                <Combobox.OptionLabel>{text}</Combobox.OptionLabel>
                            </Flex>
                        </Combobox.Option>
                    ))}
                </Combobox.Content>
            </Combobox>
            {editModal}
            {deleteModal}
        </Flex>
    );
};
